import React, { useEffect, useRef } from "react";
import { observer } from "mobx-react-lite";
import { container } from "@/utils/mobx";
import { ContextMenu } from "@/components/toolkit/ContextMenu";
import { ContextMenuOption } from "@/components/toolkit/ContextMenu/store";
import { Badge } from "@/components/toolkit/Badge";
import { TextInput } from "@/components/toolkit/TextInput";
import { isTouchDevice } from "@/utils/device";
import { SelectInputStore, SelectInputOption } from "./store";
import "./s.select_input.css";


export type { SelectInputOption };

export const SelectInput = container((props: {
    store: SelectInputStore;
    value: string | null;
    options: SelectInputOption[];
    onChange?: (value: string | null) => any;
    className?: string;
    placeholder?: string;
    searchable?: boolean;
    searchPlaceholder?: string;
    allowCustom?: boolean;
    customTitle?: (query: string) => string;
    emptyText?: string;
}) => {
    const store = props.store;
    const displayTitle = store.getTitle(store.value) || store.placeholder || "";

    return (
        <div
            className={
                "select_input" +
                (store.className ? " " + store.className : "") +
                (store.isPopupVisible ? " expanded" : "")
            }
        >
            {store.searchable ? (
                store.isPopupVisible && <SearchPopup store={store} />
            ) : (
                <ContextMenu
                    isVisible={store.isPopupVisible && !!(store.options && store.options.length)}
                    options={store.options as ContextMenuOption[]}
                    onClose={store.hidePopup}
                    onSelect={store.changeValue}
                />
            )}
            <Badge
                className="select_input_wrapper"
                onClick={() => store.togglePopup()}
            >
                <span className={"select_input__value" + (!store.getTitle(store.value) && store.placeholder ? " select_input__value--placeholder" : "")}>{displayTitle}</span>
                <span className="select_input__arrow" />
            </Badge>
        </div>
    );
}, SelectInputStore);


const SearchPopup = observer((props: { store: SelectInputStore }) => {
    const store = props.store;
    const listRef = useRef<HTMLUListElement>(null);
    const options = store.filteredOptions;

    useEffect(() => {
        const el = listRef.current?.querySelector<HTMLElement>(".select_input__option.focused");
        el?.scrollIntoView({ block: "nearest" });
    }, [store.focusedIndex, options.length]);

    let lastGroup: string | null | undefined = undefined;
    return (
        <>
            <div className="select_input__backdrop" onMouseDown={store.hidePopup} onTouchStart={store.hidePopup} />
            <div className="select_input__popup" onKeyDown={store.keyDown}>
                <TextInput
                    className="select_input__search"
                    value={store.query}
                    onChange={store.setQuery}
                    placeholder={store.searchPlaceholder}
                    // On touch the on-screen keyboard would cover the list; let users tap first.
                    autoFocus={!isTouchDevice()}
                />
                <ul className="select_input__list" ref={listRef}>
                    {options.length === 0 && (
                        <li className="select_input__empty">{store.emptyText}</li>
                    )}
                    {options.map((option, i) => {
                        const header = option.group && option.group !== lastGroup
                            ? <li key={`g:${option.group}:${i}`} className="select_input__group">{option.group}</li>
                            : null;
                        lastGroup = option.group;
                        return (
                            <React.Fragment key={option.value}>
                                {header}
                                <li
                                    className={
                                        "select_input__option" +
                                        (option.isDisabled ? " disabled" : "") +
                                        (store.focusedIndex === i ? " focused" : "") +
                                        (store.value === option.value ? " selected" : "")
                                    }
                                    onClick={() => !option.isDisabled && store.changeValue(option.value)}
                                    onMouseEnter={() => store.focusOption(i)}
                                >
                                    <span className="select_input__option_title">{option.title}</span>
                                    {option.hint && <span className="select_input__option_hint">{option.hint}</span>}
                                </li>
                            </React.Fragment>
                        );
                    })}
                </ul>
            </div>
        </>
    );
});
